import { Block, Typography } from '@aloft/toolkit-sui';
import { SortingState, Table } from '@tanstack/react-table';

import { Dispatch, SetStateAction, useState } from 'react';

import { selectPropertyIdentificationById } from '../../../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import {
  LocationType,
  formatProximityRequirementRadiusTooSmall,
  searchExceedsProximityRequirement,
} from '../../../../utils/locationTypeProximityRequirementMiles.ts';
import { DEFAULT_SELECTED_STATUSES } from '../../../map/constants.ts';
import { PinStatusFilter } from '../../../map/types.ts';
import { useRecCompSelectionContext } from '../../RecCompSelectionContext.tsx';
import { useCompMap } from '../../hooks/useCompMap.ts';
import { useGeoSearchFilterControls } from '../../hooks/useGeoSearchFilterControls.ts';
import { CompSelectionProperty, CompSelectionState } from '../../types.ts';
import { MapAlert } from './MapAlert.tsx';
import { MapViewsOverlay } from './MapViewsOverlay.tsx';
import { ProximityFilterOverlay } from './ProximityFilterOverlay.tsx';

type PropertyCompSelectionDetailsProps = {
  property: Pick<CompSelectionProperty, 'id' | 'latitude' | 'longitude'>;
  subject: Pick<CompSelectionProperty, 'latitude' | 'longitude'>;
  data: CompSelectionProperty[];
  comps: CompSelectionProperty[];
  compSelectionTableRef?: React.MutableRefObject<Table<CompSelectionProperty> | undefined>;
  sorting: SortingState;
  setFocusedRow: Dispatch<SetStateAction<string | undefined>>;
  hoveredPropertyId?: string;
  setHoveredPropertyId: Dispatch<SetStateAction<string | undefined>>;
  rowSelection?: CompSelectionState;
  isInspectifyOrder?: boolean;
  hasArvOption?: boolean;
  compSelectionTableVariant?: 'sales' | 'rental';
  isFilterPanelOpen?: boolean;
  proximityRequirementMiles?: number;
};

export const PropertyCompSelectionDetails = ({
  property: { id, latitude = 0, longitude = 0 },
  subject: { latitude: subjectLat = 0, longitude: subjectLng = 0 },
  data,
  comps,
  compSelectionTableRef,
  sorting,
  setFocusedRow,
  hoveredPropertyId,
  setHoveredPropertyId,
  rowSelection,
  isInspectifyOrder,
  hasArvOption,
  compSelectionTableVariant = 'sales',
  isFilterPanelOpen,
  proximityRequirementMiles,
}: PropertyCompSelectionDetailsProps) => {
  const [mapType, setMapType] = useState<string>('satellite');
  const [selectedStatuses, setSelectedStatuses] = useState<PinStatusFilter[]>(() => [
    ...DEFAULT_SELECTED_STATUSES,
  ]);
  const { visibleMapProperties } = useRecCompSelectionContext();
  const mapData = visibleMapProperties ?? data;
  const subjectPropertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const locationType = subjectPropertyIdentification?.locationType as LocationType | undefined;

  const {
    geoSearch,
    radius,
    setRadius,
    handleRadiusChange,
    handleGeoSearchTypeChange,
    onPolygonCreateAndUpdate,
    onPolygonDrawDelete,
  } = useGeoSearchFilterControls({
    subjectLat,
    subjectLng,
  });

  const { mapContainerRef, clearPolygon } = useCompMap({
    selectedPropertyId: id,
    lat: latitude,
    lng: longitude,
    subjectLat,
    subjectLng,
    data: mapData,
    comps,
    compSelectionTableRef,
    sorting,
    setFocusedRow,
    hoveredPropertyId,
    onHoverPropertyChange: setHoveredPropertyId,
    mapType,
    rowSelection,
    isInspectifyOrder,
    hasArvOption,
    selectedStatuses,
    onSelectedStatusesChange: setSelectedStatuses,
    compSelectionTableVariant,
    isFilterPanelOpen,
    geoSearch,
    proximityRequirementMiles,
    onPolygonCreateAndUpdate,
    onPolygonDrawDelete,
  });

  const showProximityWarning =
    locationType !== undefined &&
    proximityRequirementMiles !== undefined &&
    searchExceedsProximityRequirement({
      geoSearch,
      subjectLat,
      subjectLng,
      proximityRequirementMiles,
    });

  return (
    <Block height="100%" width="100%" position="relative">
      {showProximityWarning && (
        <MapAlert>
          <Typography variant="body1" color="textOffset">
            {formatProximityRequirementRadiusTooSmall(locationType, proximityRequirementMiles)}
          </Typography>
        </MapAlert>
      )}

      {isFilterPanelOpen && (
        <ProximityFilterOverlay
          geoSearch={geoSearch}
          radius={radius}
          onRadiusChange={handleRadiusChange}
          onRadiusClear={() => setRadius('')}
          onGeoSearchTypeChange={handleGeoSearchTypeChange}
          onPolygonClear={clearPolygon}
        />
      )}

      <MapViewsOverlay mapType={mapType} onChange={setMapType} />
      <Block ref={mapContainerRef} height="100%" width="100%" />
    </Block>
  );
};
